import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import type { DraftNotebookKind } from '../../utils/draftNotebookKind';
import type {
  PortalEventPayload,
  PortalMoviePayload,
  PortalRecipePayload,
} from '../../portal/portalDraftEnvelope';
import {
  ensurePortalEnvelope,
  serializePortalDraft,
  tryParsePortalDraft,
} from '../../portal/portalDraftEnvelope';
import { EventDraftPanel } from './EventDraftPanel';
import { MovieDraftPanel } from './MovieDraftPanel';
import { RecipeDraftPanel } from './RecipeDraftPanel';

export type StructuredDraftPanelProps = {
  notebookId: string;
  kind: Exclude<DraftNotebookKind, 'article'>;
  draftTitle: string;
  draftContent: string;
  onTitleChange: (title: string) => void;
  onSaveBody: (title: string, body: string) => void;
  /** Optional slot above the form (e.g. related material). Library leaves this unset for recipe/movie/event. */
  sourceClustersSlot?: React.ReactNode;
  /** Creator + access summary (Library drawer). */
  draftAccessBar?: React.ReactNode;
  /** When true, title + access live in the drawer header (Library). */
  accessInHeader?: boolean;
  readOnly?: boolean;
};

export function StructuredDraftPanel({
  notebookId,
  kind,
  draftTitle,
  draftContent,
  onTitleChange,
  onSaveBody,
  sourceClustersSlot,
  draftAccessBar,
  accessInHeader = false,
  readOnly = false,
}: StructuredDraftPanelProps) {
  const [recipe, setRecipe] = useState<PortalRecipePayload>(() => {
    const e = ensurePortalEnvelope(draftContent, draftTitle, notebookId, 'recipe');
    return e.kind === 'recipe' ? e.payload : ({} as PortalRecipePayload);
  });
  const [movie, setMovie] = useState<PortalMoviePayload>(() => {
    const e = ensurePortalEnvelope(draftContent, draftTitle, notebookId, 'movie');
    return e.kind === 'movie' ? e.payload : ({} as PortalMoviePayload);
  });
  const [event, setEvent] = useState<PortalEventPayload>(() => {
    const e = ensurePortalEnvelope(draftContent, draftTitle, notebookId, 'event');
    return e.kind === 'event' ? e.payload : ({} as PortalEventPayload);
  });

  const t = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleSave = useCallback(
    (title: string, envelopeJson: string) => {
      if (t.current) clearTimeout(t.current);
      t.current = setTimeout(() => onSaveBody(title, envelopeJson), 850);
    },
    [onSaveBody]
  );

  useEffect(
    () => () => {
      if (t.current) clearTimeout(t.current);
    },
    []
  );

  useEffect(() => {
    const parsed = tryParsePortalDraft(draftContent);
    if (parsed && parsed.kind === kind) {
      if (parsed.kind === 'recipe') setRecipe(parsed.payload);
      else if (parsed.kind === 'movie') setMovie(parsed.payload);
      else if (parsed.kind === 'event') setEvent(parsed.payload);
      return;
    }
    const e = ensurePortalEnvelope(draftContent, draftTitle, notebookId, kind);
    if (e.kind === 'recipe') setRecipe(e.payload);
    else if (e.kind === 'movie') setMovie(e.payload);
    else if (e.kind === 'event') setEvent(e.payload);
    // draftTitle intentionally omitted from deps: it updates before debounced draftContent;
    // syncing on draftTitle would reset local payload from stale JSON and break title typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notebookId, kind, draftContent]);

  const saveRecipe = (next: PortalRecipePayload) => {
    if (readOnly) return;
    setRecipe(next);
    const title = next.title || 'Untitled recipe';
    onTitleChange(title);
    scheduleSave(title, serializePortalDraft({ schemaVersion: 1, kind: 'recipe', payload: next }));
  };

  const saveMovie = (next: PortalMoviePayload) => {
    if (readOnly) return;
    setMovie(next);
    const title = next.movie_title || 'Untitled movie';
    onTitleChange(title);
    scheduleSave(title, serializePortalDraft({ schemaVersion: 1, kind: 'movie', payload: next }));
  };

  const saveEvent = (next: PortalEventPayload) => {
    if (readOnly) return;
    const updated = { ...next, updated_at: new Date().toISOString() };
    setEvent(updated);
    const title = updated.title || 'Untitled event';
    onTitleChange(title);
    scheduleSave(title, serializePortalDraft({ schemaVersion: 1, kind: 'event', payload: updated }));
  };

  if (kind === 'recipe') {
    return (
      <RecipeDraftPanel
        recipe={recipe}
        saveRecipe={saveRecipe}
        sourceClustersSlot={sourceClustersSlot}
        draftAccessBar={accessInHeader ? undefined : draftAccessBar}
        accessInHeader={accessInHeader}
        readOnly={readOnly}
      />
    );
  }

  if (kind === 'movie') {
    return (
      <MovieDraftPanel
        movie={movie}
        saveMovie={saveMovie}
        sourceClustersSlot={sourceClustersSlot}
        draftAccessBar={accessInHeader ? undefined : draftAccessBar}
        accessInHeader={accessInHeader}
        readOnly={readOnly}
      />
    );
  }

  return (
    <Box sx={{ minHeight: 500 }}>
      <EventDraftPanel
        notebookId={notebookId}
        event={event}
        saveEvent={saveEvent}
        sourceClustersSlot={sourceClustersSlot}
        draftAccessBar={accessInHeader ? undefined : draftAccessBar}
        accessInHeader={accessInHeader}
        readOnly={readOnly}
      />
    </Box>
  );
}
